import { useEffect, useMemo, useState } from "react";
import { useLang } from "../lib/useLang";
import { fetchMenuItems, subscribeDailyItems } from "../lib/catalog";
import { CATEGORY_META } from "../data/menu";
import { dateKey } from "../lib/format";
import type { DailyItem, MenuItem } from "../lib/types";
import { ItemCard } from "./ItemCard";
import { DailyItemCard } from "./DailyItemCard";
import { SectionHeader } from "./SectionHeader";
import { CategoryNav, type NavItem } from "./CategoryNav";
import { SmartImage } from "./SmartImage";
import { categoryImage } from "../data/images";

/**
 * The full ordering catalog: sticky scroll-spy category rail, a horizontal strip of
 * today's fresh trays, then every standing-menu category as its own section — all in
 * one continuous scroll.
 */
export function MenuCatalog() {
  const { t, pick } = useLang();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [daily, setDaily] = useState<DailyItem[]>([]);

  useEffect(() => {
    fetchMenuItems().then(setItems);
  }, []);
  useEffect(() => subscribeDailyItems(dateKey(), setDaily), []);

  const cats = useMemo(
    () => CATEGORY_META.filter((c) => items.some((i) => i.category === c.key)),
    [items],
  );

  const navItems: NavItem[] = [
    ...(daily.length ? [{ key: "daily", label: t("nav.daily"), icon: "🍱" }] : []),
    ...cats.map((c) => ({ key: c.key, label: pick(c.en, c.ar), icon: c.icon })),
  ];

  return (
    <div>
      <CategoryNav items={navItems} />

      {daily.length > 0 && (
        <section id="sec-daily" className="scroll-mt-28 pt-5">
          <div className="rounded-3xl bg-gradient-to-b from-gold/10 via-gold/[0.04] to-transparent p-4 ring-1 ring-gold/20 sm:p-5">
            <SectionHeader
              icon="🍱"
              title={t("menu.dailyTitle")}
              kicker={t("menu.dailyTag")}
            />
            <p className="mb-3 -mt-1 text-sm text-black/50">{t("home.dailyNote")}</p>
            <div className="no-scrollbar -mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-1 sm:-mx-5 sm:px-5">
              {daily.map((it) => (
                <DailyItemCard key={it.id} item={it} />
              ))}
            </div>
          </div>
        </section>
      )}

      {cats.map((c) => (
        <section key={c.key} id={`sec-${c.key}`} className="scroll-mt-28 pt-7">
          {/* Category banner */}
          <div className="img-skeleton relative mb-4 h-24 overflow-hidden rounded-2xl shadow-soft">
            <SmartImage
              src={categoryImage(c.key)}
              alt=""
              className="h-full w-full object-cover"
              fallback={<div className="h-full w-full bg-gradient-to-r from-brand to-brand-dark" />}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center gap-3 px-5">
              <span className="text-3xl drop-shadow">{c.icon}</span>
              <h2 className="display text-2xl text-white drop-shadow">{pick(c.en, c.ar)}</h2>
            </div>
          </div>
          {/* One column on mobile (reads as a list), 2–3 columns on desktop to
              fill the width instead of one long thin column. */}
          <div className="grid grid-cols-1 gap-x-10 lg:grid-cols-2 xl:grid-cols-3">
            {items
              .filter((i) => i.category === c.key)
              .map((it) => (
                <ItemCard key={it.id} item={it} />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}
